import { paths } from "@/api-schema";
import {
  PathsV1TemporaryPermissionSearchPostParametersQueryExtend,
  TemporaryPermissionStatus,
} from "@/api-schema-enums";
import { useTemporaryPermissionUpdateMutation } from "@/common/api/temporary-permission/use-temporary-permission-update.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { useUrlStateSlice } from "@/common/hooks/use-url-state-slice";
import { TemporaryPermissionTable } from "@/components/data-table/predefined/temporary-permission-table";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { StatusSelect } from "./_components/status-select";

const STATUS_FILTER_OPTIONS = [
  TemporaryPermissionStatus.Pending,
  TemporaryPermissionStatus.Approved,
  TemporaryPermissionStatus.Rejected,
] as const;

type StatusFilter = (typeof STATUS_FILTER_OPTIONS)[number];

export function ManageView() {
  const { t } = useTranslation(["ram", "common"]);
  const [rawStatusFilter, setStatusFilter] = useUrlStateSlice<StatusFilter>("status");
  const statusFilter =
    rawStatusFilter !== undefined && STATUS_FILTER_OPTIONS.includes(rawStatusFilter) ? rawStatusFilter : undefined;

  const { mutateAsync } = useTemporaryPermissionUpdateMutation();

  const body = useMemo<paths["/v1/temporary-permission/search"]["post"]["requestBody"]["content"]["application/json"]>(
    () => ({
      status: statusFilter ? [statusFilter] : undefined,
    }),
    [statusFilter],
  );
  const query = useMemo<paths["/v1/temporary-permission/search"]["post"]["parameters"]["query"]>(
    () => ({
      extend: [
        PathsV1TemporaryPermissionSearchPostParametersQueryExtend.RequestFor,
        PathsV1TemporaryPermissionSearchPostParametersQueryExtend.RequestBy,
        PathsV1TemporaryPermissionSearchPostParametersQueryExtend.DecidedBy,
      ],
    }),
    [],
  );

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("ram:TemporaryPermissions")}
      subTitle={t("ram:TemporaryPermissionsDescription")}
    >
      <TemporaryPermissionTable
        body={body}
        query={query}
        headerComponent={
          <div>
            <StatusSelect
              value={statusFilter ?? ""}
              onValueChange={(value) => {
                switch (value) {
                  case TemporaryPermissionStatus.Pending:
                  case TemporaryPermissionStatus.Approved:
                  case TemporaryPermissionStatus.Rejected:
                    setStatusFilter(value);
                    break;
                  default:
                    setStatusFilter(undefined);
                }
              }}
            />
          </div>
        }
        onApprove={(temporaryPermissionUuid) => {
          toastMutationStatus({
            mutationFn: () =>
              mutateAsync({ uuid: temporaryPermissionUuid, status: TemporaryPermissionStatus.Approved }),
            successMessage: t("ram:RequestApproved"),
            errorMessage: t("ram:RequestCouldNotBeApproved"),
          });
        }}
        onReject={(temporaryPermissionUuid) => {
          toastMutationStatus({
            mutationFn: () =>
              mutateAsync({ uuid: temporaryPermissionUuid, status: TemporaryPermissionStatus.Rejected }),
            successMessage: t("ram:RequestRejected"),
            errorMessage: t("ram:RequestCouldNotBeRejected"),
          });
        }}
      />
    </CenteredLayout>
  );
}
